import { useCallback, useEffect, useRef, useState } from 'react'
import { isCarouselWidgetType } from '../constants/widgetTypes'
import { createWidget, deleteWidget, Widget } from '../services/api'
import { DirtyTracker, useDirtyTracker } from './useDirtyTracker'

// Widget types that need a DB row before the form can be finished: the image
// endpoints key artwork by widget_id, so there is nowhere to put a file until
// the row exists.
const AUTO_CREATE_TYPES = new Set<string>(['banner_image', 'hero_banner'])

// Plus the card carousels and the grid, whose custom pins upload to
// /widgets/{id}/pin-images (CarouselPinsEditor, shared by both). Every editor
// rendering an ImageSlot gated on widgetId must be covered here, or naming the
// widget never unlocks the slot and its "name the widget to unlock uploads"
// hint never clears.
const needsAutoCreate = (type: string): boolean =>
  AUTO_CREATE_TYPES.has(type)
  || isCarouselWidgetType(type)
  || type === 'standard_landscape_grid'

// The draft row is created without its title, deliberately. A carousel or grid
// mints its public collection slug from metadata.title on INSERT (migration
// 111's is_card_carousel_family), and WidgetForm mirrors the internal Name into
// Title as you type — so posting the title here would publish
// /collections/<slugified-internal-name>/ the moment the Name field blurs,
// before any save, and a tab closed at that point leaks both the draft and a
// live URL. The explicit save PUTs the real title, and the UPDATE trigger mints
// the slug then.
const withoutTitle = (metadata: Record<string, unknown>): Record<string, unknown> => {
  const next = { ...metadata }
  delete next.title
  return next
}

export interface WidgetFormState {
  // Controlled values
  name: string
  setName: (v: string) => void
  type: string
  setType: (v: string) => void
  metadata: Record<string, unknown>
  setMetadata: (v: Record<string, unknown>) => void

  // The widget's DB id, once one exists. Populated from initialWidget on
  // edit, or when auto-create succeeds on a new banner_image widget.
  widgetId: string | null

  // True iff the widget was born in this session (via auto-create). Used to
  // decide whether a cancel should DELETE the row.
  autoCreated: boolean

  // True while the auto-create POST is in flight.
  autoCreating: boolean

  // True when the underlying widget should be treated as locked — initial
  // edit mode, or the widget has been auto-created and committed.
  typeLocked: boolean

  // Wire to the Name input's onBlur. Triggers auto-create when appropriate.
  handleNameBlur: () => void

  // Call when the form is closed without an explicit save. Deletes the
  // widget if we auto-created it this session.
  discardIfAutoCreated: () => void

  // Call after an explicit save (widget and/or placement) completes, so the
  // auto-created widget is no longer treated as a draft.
  markCommitted: () => void

  // Reset to a new initial state (e.g. drawer reopen, tab switch).
  reset: (initialWidget?: Widget | null) => void

  // Forget the dirty baseline without touching the values. This is what a
  // close path wants: reset() would also blank name/type/metadata and clear
  // the closed-while-in-flight guard, which only looks harmless when the
  // component unmounts a tick later.
  clearDirty: () => void

  // Spread onto the element wrapping the form. Establishes the baseline the
  // unsaved-changes guard compares against on the user's first interaction.
  // WidgetForm wires this up, so consumers rendering it get it for free.
  touchProps: DirtyTracker['touchProps']

  // True when name/type/metadata have moved away from the baseline. Read it
  // before closing a form that isn't a route.
  isDirty: () => boolean
}

interface Options {
  siteId: string
  initialWidget?: Widget | null
  // Called after auto-create or discard so the parent can refresh its
  // widget list (typically an SWR mutate).
  onWidgetsChanged?: () => void | Promise<void>
}

export function useWidgetFormState({ siteId, initialWidget, onWidgetsChanged }: Options): WidgetFormState {
  const [widget, setWidget] = useState<Widget | null>(initialWidget ?? null)
  const [name, setName] = useState(initialWidget?.name ?? '')
  const [type, setType] = useState(initialWidget?.widget_type ?? '')
  const [metadata, setMetadata] = useState<Record<string, unknown>>(initialWidget?.metadata ?? {})
  const [autoCreated, setAutoCreated] = useState(false)
  const [autoCreating, setAutoCreating] = useState(false)

  const dirty = useDirtyTracker(() => ({ name, type, metadata }))

  // One-shot guard — don't keep trying to auto-create every time the name
  // field blurs. Reset on reset().
  const autoCreateAttemptedRef = useRef(false)
  // Closed-while-in-flight safety — if the user cancels during the POST,
  // clean up the widget that the server just created.
  const closedRef = useRef(false)

  const fireOnChange = useCallback(async () => {
    if (onWidgetsChanged) await onWidgetsChanged()
  }, [onWidgetsChanged])

  const handleNameBlur = useCallback(async () => {
    if (autoCreateAttemptedRef.current) return
    if (widget) return
    if (!needsAutoCreate(type)) return
    if (!name.trim()) return

    autoCreateAttemptedRef.current = true
    setAutoCreating(true)
    try {
      const created = await createWidget({
        site_id: siteId,
        widget_type: type,
        name: name.trim(),
        metadata: withoutTitle(metadata),
      })
      if (closedRef.current) {
        void deleteWidget(created.id).catch(() => {})
        return
      }
      setWidget(created)
      setAutoCreated(true)
      await fireOnChange()
    } catch (err) {
      autoCreateAttemptedRef.current = false
      // Re-throw so the caller can surface a toast if desired.
      throw err
    } finally {
      setAutoCreating(false)
    }
  }, [name, type, metadata, siteId, widget, fireOnChange])

  const discardIfAutoCreated = useCallback(() => {
    // Idempotent: a form's own close path and the unmount cleanup below can
    // both run for one exit, and the widget must only be DELETEd once.
    if (closedRef.current) return
    closedRef.current = true
    dirty.reset()
    if (autoCreated && widget) {
      // Fire-and-forget — caller is already navigating away.
      void deleteWidget(widget.id).then(() => fireOnChange()).catch(() => {})
    }
  }, [autoCreated, widget, fireOnChange, dirty])

  const markCommitted = useCallback(() => {
    setAutoCreated(false)
    // The values that were just submitted become the new baseline, so closing
    // straight after a save doesn't prompt.
    dirty.markSaved()
  }, [dirty])

  // An auto-created widget is a draft owned by this form, so it must not
  // outlive it. Consumers discard it on their own close paths, but a form can
  // also leave by unmounting — PlacementDetail is a route, so the back
  // button and the router blocker's Discard both end here without running its
  // close handler, and a drawer that is still open when its page unmounts
  // never runs one either.
  const discardRef = useRef(discardIfAutoCreated)
  discardRef.current = discardIfAutoCreated
  useEffect(() => () => discardRef.current(), [])

  const reset = useCallback((next?: Widget | null) => {
    closedRef.current = false
    dirty.reset()
    autoCreateAttemptedRef.current = false
    setAutoCreated(false)
    setAutoCreating(false)
    setWidget(next ?? null)
    setName(next?.name ?? '')
    setType(next?.widget_type ?? '')
    setMetadata((next?.metadata ?? {}) as Record<string, unknown>)
  }, [dirty])

  return {
    name, setName,
    type, setType,
    metadata, setMetadata,
    widgetId: widget?.id ?? null,
    autoCreated,
    autoCreating,
    typeLocked: widget != null,
    handleNameBlur,
    discardIfAutoCreated,
    markCommitted,
    reset,
    clearDirty: dirty.reset,
    touchProps: dirty.touchProps,
    isDirty: dirty.isDirty,
  }
}
